
import { RevContainer } from '@ds/components/Container'
import isEmpty from 'lodash/isEmpty'
import { parse } from 'query-string'
import { mapGetters } from 'vuex'

import SkinnyBanner from '@core/components/SkinnyBanner'
import { prepareJsonLdScript, removeEmptyValuesInObject } from '@core/helpers'
import {
  SCHEMA_PRODUCT_IMAGE,
  buildProductSchema,
  generateAlternateLink,
  getMetas,
} from '@core/helpers/head'
import { fetchLandingBuyingGuides, fetchSEOTags } from '@http/endpoints'
import {
  REVIEWS_TYPES,
  getRateDistributionRequest,
  getReviewsListRequest,
} from '@internal-reviews/helpers'
import { logger } from '@recommendation/helpers'
import { MAX_RATING } from '@reviews/constants'
import { logReviewsError } from '@reviews/helpers'
import { getDevice } from '@tracking'

import ProductResultsSection from '../../components/ProductResults/ProductResultsSection.vue'
import SearchParams, { getSourceFromRouteName } from '../../utils/SearchParams'
import { createCanonicalPageQueryString } from '../helpers'

import translations from './ProductLanding.translations'
import Guides from './components/Guides'
import ProductTags from './components/ProductTags'
import Reviews from './components/Reviews'
import SEOText from './components/SEOText'
import TopLanding from './components/TopLanding'

async function fetchTopLanding({ route, store, redirect }) {
  await store.dispatch('landing/isLoadingProductLanding', true)

  const queryParams = { id: route.params.uuid, slug: route.params.slugV2 }

  await Promise.all([
    store.dispatch('landing/fetchProductLanding', queryParams),
    store.dispatch('landing/fetchLandingBreadcrumb', queryParams),
    store
      .dispatch('landing/fetchLandingSeoNavigation', queryParams)
      .catch(() => ({
        payload: {},
      })),
    store
      .dispatch('landing/fetchLandingGlobalRate', {
        ...queryParams,
        type: REVIEWS_TYPES.LANDING,
      })
      .catch(() => ({
        payload: {},
      })),
  ])

  const countryCode = store.getters['config/country']
  const link = store.getters['landing/links'][countryCode]
  if (link && route.params.slugV2 !== link.params.slugV2) {
    redirect({
      type: 'internal',
      name: link.name,
      params: {
        locale: route.params.locale,
        slugV2: link.params.slugV2,
        uuid: link.params.uuid,
      },
    })
  }

  await store.dispatch('landing/isLoadingProductLanding', false)

  return {}
}

async function fetchLayout({ store, route }) {
  store.dispatch('landing/reset')
  await store.dispatch('landingSearch/fetchResults', {
    searchParams: new SearchParams({
      query: route.query,
      hash: route.hash,
      context: {
        source: getSourceFromRouteName(route.name),
        slug: route.params.merchantName,
        id: route.params.uuid,
      },
      paramsWhitelist: store.getters['landingSearch/filterWhitelist'],
      tags: [getDevice(), getSourceFromRouteName(route.name)],
    }),
  })
}

export default {
  name: 'ProductLanding',
  components: {
    ProductTags,
    SEOText,
    Reviews,
    Guides,
    RevContainer,
    TopLanding,
    SkinnyBanner,
    ProductResultsSection,
  },
  layout: 'empty',

  async asyncData(args) {
    const { app, store, route, redirect } = args
    const { getters, dispatch } = store

    // Previously, we rendered the first page with ?page=1 query string in the Pagination component.
    // Since we removed it, let's redirect the page to keep the SEO we built on it and avoid duplicated content.
    if (route.query.page && parseInt(route.query.page, 10) === 1) {
      redirect(301, {
        name: route.name,
        params: route.params,
        query: removeEmptyValuesInObject({ ...route.query, page: undefined }),
      })

      return {}
    }

    try {
      await Promise.all([fetchLayout(args), fetchTopLanding(args)])
    } catch (error) {
      logger.info(error, {
        component: { name: 'ProductLanding', props: null },
        routeName: args.route.name,
      })

      if (error.status_code === 429) {
        // Make sure to let 429 errors through, in order to improve the caching
        // strategy on Cloudflare side.
        args.error({
          statusCode: error.status_code,
          message: error.errorMessage,
        })

        return {}
      }

      args.error({ statusCode: 404, message: error.errorMessage })

      return {}
    }

    const baseUrl = getters['config/baseURL']
    const buildAlternateLinks = Object.entries(getters['landing/links']).map(
      ([country, link]) => {
        return generateAlternateLink({ link, country, baseUrl })
      },
    )

    const landingId = route.params.uuid || store.getters['landing/landingId']

    const fetchTags = dispatch('http/request', {
      request: fetchSEOTags,
      queryParams: {
        model: 'landing',
        pk: landingId,
      },
    })

    const fetchGuides = dispatch('http/request', {
      request: fetchLandingBuyingGuides,
      pathParams: { landingId },
    })

    const fetchRateDistribution = dispatch(
      'http/request',
      getRateDistributionRequest({
        type: REVIEWS_TYPES.LANDING,
        params: { id: landingId },
      }),
    )

    const fetchReviews =
      app.$config.KILL_REVIEW_LANDING_PAGE === false
        ? dispatch(
            'http/request',

            getReviewsListRequest({
              type: REVIEWS_TYPES.LANDING,
              params: { id: landingId },
              route,
              limit: MAX_RATING,
            }),
          ).catch((err) => {
            logReviewsError({ tags: ['LandingPage'], err })

            return { payload: { results: [] } }
          })
        : Promise.resolve({
            payload: { results: [] },
          })

    const [tags, guides, reviews, rateDistribution] = (
      await Promise.allSettled([
        fetchTags,
        fetchGuides,
        fetchReviews,
        fetchRateDistribution,
      ])
    ).map(({ status, value }) => {
      if (status === 'fulfilled') return value?.payload

      return undefined
    })

    return removeEmptyValuesInObject({
      alternateLinks: await Promise.all(buildAlternateLinks),
      tags,
      guides,
      reviews: reviews?.results,
      rateDistribution,
    })
  },

  data() {
    return {
      alternateLinks: [],
      guides: [],
      tags: [],
      reviews: [],
      rateDistribution: {},
      isLoading: false,
    }
  },

  head() {
    const title = this.metaTitle

    const description = this.$t(translations.metaDescription, {
      productType: title,
      warrantyDuration: this.warrantyDuration,
      coolingOffDays: this.coolingOffDays,
    })

    const image = this.$static(SCHEMA_PRODUCT_IMAGE)

    const path = decodeURIComponent(this.$route.path)

    const productSchema = buildProductSchema({
      title,
      description,
      rate: this.isRatingsAvailable ? this.ratings : {},
      image,
    })

    const page = createCanonicalPageQueryString(this.$route.query)

    return {
      title: this.seoTitleWithPage,
      meta: [
        ...getMetas({
          title,
          description,
          image,
          'og:type': 'product',
        }),
      ],
      link: [
        ...this.alternateLinks,
        {
          href: `${this.baseURL}${path}${page}`,
          rel: 'canonical',
        },
      ],
      script: prepareJsonLdScript({ productSchema }),
      __dangerouslyDisableSanitizers: ['script'],
    }
  },

  computed: {
    ...mapGetters({
      landingTitle: 'landing/title',
      seoTitle: 'landing/seoTitle',
      metaTitle: 'landing/subtitle',
      ratings: 'landing/ratings',
      baseURL: 'config/baseURL',
      warrantyDuration: 'config/warrantyDuration',
      coolingOffDays: 'config/coolingOffDays',
      filterWhitelist: 'landingSearch/filterWhitelist',
      searchHasResults: 'landingSearch/hasResults',
      isCurrentContextId: 'landingSearch/isCurrentContextId',
      isSkinnyBannerAvailable: 'countryConfig/isSkinnyBannerAvailable',
      isLoadingProductLanding: 'landing/isLoading',
    }),

    searchContext() {
      const { params, name } = this.$route

      return removeEmptyValuesInObject({
        source: getSourceFromRouteName(name),
        slug: params?.merchantName,
        id: params.uuid,
      })
    },
    searchParams() {
      const { query, hash, name } = this.$route

      return new SearchParams({
        query,
        hash,
        context: this.searchContext,
        paramsWhitelist: this.filterWhitelist,
        tags: [getDevice(), getSourceFromRouteName(name)],
      })
    },
    searchFilters() {
      return this.searchParams.toFilters()
    },
    hasFilters() {
      return this.searchParams.hasUserFilters()
    },

    seoTitleWithPage() {
      const searchCurrentPage = this.$route.query?.page

      if (searchCurrentPage > 1) {
        return this.$t(translations.seoTitle, {
          title: this.seoTitle,
          pageNumber: searchCurrentPage,
        })
      }

      return this.seoTitle
    },

    isRatingsAvailable() {
      return (
        this.$config.KILL_REVIEW_LANDING_PAGE === false &&
        !isEmpty(this.ratings) &&
        this.ratings.averageRate > 0 &&
        this.ratings.reviewsCount > 0
      )
    },
  },

  watch: {
    '$route.hash': 'fetchSearchResults',
    '$route.query': 'fetchSearchResults',
  },

  mounted() {
    if (this.hasFilters) {
      this.fetchSearchResults()
    }
  },

  methods: {
    async fetchSearchResults() {
      this.isLoading = true

      await this.$store.dispatch('landingSearch/fetchResults', {
        searchParams: this.searchParams,
      })

      this.isLoading = false
    },

    updateQueryString(filter) {
      const filters = {
        ...this.searchFilters,
        page: 1,
        context: this.searchContext,
        paramsWhitelist: this.filterWhitelist,
        ...filter,
      }

      const query = SearchParams.fromFilters(filters).toQueryParams()

      this.updateURLFromFilters(query)
    },

    resetFilters() {
      const { q } = this.searchFilters
      this.updateURLFromFilters({
        ...(q && { query: { q } }),
      })
    },

    updateURLFromFilters({ hash, query }) {
      this.$navigation.push({
        name: this.$route.name,
        params: this.$route.params,
        hash: parse(hash),
        query,
        type: 'internal',
      })
    },
  },
}
